import React, { useState, useRef, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import { Button } from '@/app/components/ui/button'
import { Loader2, Search, AlertCircle } from 'lucide-react'
import { Product, posProductService } from '@/app/services/pos.service'
import { ProductCard } from '@/app/components/pos/ProductCard'
import { useHotkeys } from 'react-hotkeys-hook'

export interface PriceCheckModalProps {
  isOpen: boolean
  onClose: () => void
  onAddToCart: (product: Product) => void
}

export function PriceCheckModal({ isOpen, onClose, onAddToCart }: PriceCheckModalProps) {
  const [barcode, setBarcode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [product, setProduct] = useState<Product | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setBarcode('')
      setProduct(null)
      setErrorMsg(null)
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  const checkPrice = async () => {
    if (!barcode.trim()) return

    setIsLoading(true)
    setErrorMsg(null)
    setProduct(null)

    try {
      const result = await posProductService.priceCheckByBarcode(barcode.trim())
      setProduct(result)
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setErrorMsg('No se encontró ningún producto con ese código.')
      } else if (error?.response?.status === 409) {
        setErrorMsg('El código de barras coincide con más de un producto.')
      } else {
        setErrorMsg('Error al consultar el precio. Intente nuevamente.')
      }
    } finally {
      setIsLoading(false)
      // Focus input again so user can immediately scan another product
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    checkPrice()
  }

  useHotkeys('esc', () => {
    if (isOpen) {
      onClose()
    }
  }, { enableOnFormTags: true }, [isOpen, onClose])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Consulta Rápida de Precio</DialogTitle>
          <DialogDescription>
            Escanea o ingresa el código de barras del producto.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                ref={inputRef}
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="Código de barras..."
                className="pl-9"
                disabled={isLoading}
              />
            </div>
            <Button type="submit" disabled={isLoading || !barcode.trim()}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buscar'}
            </Button>
          </form>

          {errorMsg && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-orange-800 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
              <p className="text-sm font-medium">{errorMsg}</p>
            </div>
          )}

          {product && (
            <div className="pt-2">
              <ProductCard
                product={product}
                addToCart={(p) => {
                  onAddToCart(p)
                  // Optionally close after adding? 
                  // Let's just focus the input again to scan more if they want.
                  inputRef.current?.focus()
                  inputRef.current?.select()
                }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
